import { createElement as rc, useState, useCallback, useRef } from 'react';
import useEventSink from '../../hooks/useEventSink';
import { useTheme } from 'styled-components';
import { Text, Pressable, Icon } from 'lib_ui-primitives';
import useDbView from '../../hooks/useDbView';
import NotificationList from './NotificationList';
import { Title, TopButtonBar, NoNotifications, HeaderTitleContainer } from './styles.gl2025';
import DropDownButton from '../_abstractComponent/DropDownButton';

const _p = {
    useDbView,
    useEventSink,
    NotificationList
};
export const _private = _p;

const virtualhNode = {
    id: 'notificationIcon'
};

const NotificationIconGL2025 = ({ id, size }) => {
    const theme = useTheme();
    const [filterButtonRef, recoverFocus] = useFilterButtonRef();
    const [, publish] = _p.useEventSink();
    const [filter, setFilter] = useState('all'); // 'all' or 'errors'

    const buttonSize = size || 'sm';
    
    const viewName = `application_notification_${virtualhNode.id}`;
    const { records, viewCriteria } = _p.useDbView('application', 'notification', viewName, virtualhNode);
    
    viewCriteria.addSort('meta.createdTime', true, virtualhNode.id);
    viewCriteria.addFind({ 'meta.deleted': { $exists: false } });

    const notifications = records.filter(n => filter === 'errors' ? n.variant === 'error' : true);
    const hasNotifications = notifications.length > 0;
    const allRecordsSeen = notifications.every(n => n.seen === true);
    const someRecordsUnseen = records.some(n => n.seen !== true);
    const someErrorsUnseen = records.some(n => n.variant === 'error' && n.seen !== true);
    const badge = someErrorsUnseen ? 'error' : someRecordsUnseen ? 'info' : undefined;

    function resetNotifications() {
        publish(
            {},
            {
                verb: 'reset',
                namespace: 'application',
                relation: 'notification'
            }
        );
    }

    function markAllNotificationsAsSeen() {
        // Letting the rules engine know we are looking at notifications so that they can
        // all be set to 'seen': true.
        publish({}, { verb: 'view', namespace: 'application', relation: 'notification' });
    }

    function onNotificationClick(item) {
        if (!item.seen) {
            publish({ id: item._id }, { verb: 'view', namespace: 'application', relation: 'notification' });
        }
    }

    // @ts-ignore
    const { title, filterButton, actionButton } = theme.notifications.header;
    
    // Render custom header buttons
    // Note: using native buttons was first choice - but there was no way to get them to position and look right on mobile (native)
    const renderHeaderButton = ({ text, icon, font, color, onClick, ref = null }) => {
        return rc(Pressable, {
            ref,
            tabIndex: 0,
            ...(theme.mobile ? { onPress: onClick } : { onClick: onClick }),
            // @ts-ignore
            style: { 
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                ...(!theme.mobile ? { cursor: 'pointer' } : {})
            },
            className: 'notification-header-button'
        },
            // @ts-ignore
            rc(Text, {
                style: { 
                    fontFamily: font.fontFamily,
                    fontSize: font.fontSize,
                    fontWeight: font.fontWeight,
                    color: color
                }
            }, text),
            rc(Icon, { 
                name: icon, 
                size: 'sm', 
                color: color,
                fill: false
            })
        );
    };

    // prettier-ignore
    return rc(DropDownButton, {
        id,
        icon: 'notifications',
        size: buttonSize,
        color: 'gray',
        badge,
        variant: 'secondary',
        menuClassName: 'notifications-menu'
    },
        theme.mobile ? rc(Title, { style: title.mobile }, 'Notifications') : null,
        rc(TopButtonBar, { key: 'header' },
            rc(HeaderTitleContainer, null,
                !theme.mobile && rc(Title, null, 'Notifications'),
                renderHeaderButton({
                    ref: filterButtonRef,
                    text: filter === 'all' ? 'All' : 'Errors Only',
                    color: filterButton.color,
                    icon: 'expand-more',
                    font: filterButton.font,
                    onClick: e => {
                        e?.stopPropagation?.();
                        setFilter(f => f === 'all' ? 'errors' : 'all');
                    }
                })
            ),
            renderHeaderButton({
                text: allRecordsSeen ? 'Delete all' : 'Mark all as read',
                color: actionButton.color,
                icon: actionButton.icon,
                font: actionButton.font,
                onClick: allRecordsSeen ? resetNotifications : markAllNotificationsAsSeen
            })
        ),
        hasNotifications && rc(_p.NotificationList, { key: 'list', notifications, onShowSelectedNotification: onNotificationClick, recoverFocus }),
        !hasNotifications && rc(NoNotifications, { key: 'no-notes' }, 'No current notifications.')
    );
};

function useFilterButtonRef() {
    const filterButtonRef = useRef(null);
    const recoverFocus = useCallback(() => {
        // If the focus is lost (or about to be lost), move it to the filter button in the header
        // This prevents the menu from getting into a bad state where it thinks it's open but ignores outside clicks
        // because the previously focused element (the delete button) is gone.
        // Using requestAnimationFrame to ensure the DOM has updated before focusing.
        requestAnimationFrame(() => {
            if (filterButtonRef.current) {
                filterButtonRef.current.focus();
            }
        });
    }, []);
    return [filterButtonRef, recoverFocus];
}

export default NotificationIconGL2025;
